import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { isImageValid, isTimestampValid } from "../util/common.util";
import { encryptData, decryptData } from "./crypto.js";

export default {
  async getBgGeneratedImg(email, callback) {
    const db = getFirestore();
    const docRef = doc(db, "BgGeneration", email);
    try {
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data && data?.images && data?.images?.length) {
            const res = [...data.images].reverse();
            callback(res);
          } else {
            callback(null);
          }
        } else {
          callback(null);
        }
      });
      // Return the unsubscribe function to stop listening when needed
      return unsubscribe;
    } catch (e) {
      console.error("Error setting up listener:", e);
    }
  },

  async getEditedImages(email) {
    const db = getFirestore();
    const docRef = doc(db, "ImageEditor", email);
    try {
      const docSnap = await getDoc(docRef);
      const data = docSnap.data();
      if (data && data?.images && data?.images?.length) {
        const res = [...data.images].reverse();
        return res;
      } else {
        return null;
      }
    } catch (e) {
      console.error(e);
    }
  },

  async deleteExpiredImages(collection, email) {
    const db = getFirestore();
    const docRef = doc(db, collection, email);
    const currentTime = Date.now();
    const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    let data = [];
    if (collection === "BgGeneration") {
      await new Promise((resolve) => {
        this.getBgGeneratedImg(email, (imgData) => {
          data = imgData;
          resolve();
        });
      });
    } else {
      data = await this.getEditedImages(email);
    }
    if (data && data?.length) {
      const validImages = await Promise.all(
        data.map(async (item) => {
          let imageUrl;
          if (collection == "BgGeneration") {
            imageUrl = item.images[0];
          } else if (item.operation == "remove" || item.operation == "blur") {
            imageUrl = item.img;
          } else if (item.operation == "photoshoot") {
            imageUrl = item.img[0];
          }
          const isValid = await isImageValid(imageUrl);
          const isNotExpired = currentTime - item?.timestamp <= oneDay;
          return isValid && isNotExpired ? item : null;
        }),
      );
      const filteredImages = validImages.filter((item) => item !== null);
      if (filteredImages?.length !== data?.length) {
        await updateDoc(docRef, { images: filteredImages });
        console.log(
          `Expired or invalid images deleted from ${collection} for ${email}`,
        );
      }
    }
  },

  async deleteExpiredVideos(collection, email) {
    const db = getFirestore();
    const docRef = doc(db, collection, email);
    if (collection === "VideoMockup") {
      await this.getVideoMockupStatus(email, async (data) => {
        if (data && data?.length) {
          const validVideos = await Promise.all(
            data.map(async (item) => {
              return isTimestampValid(item.timestamp, 1) ? item : null;
            }),
          );
          const filteredVideos = validVideos.filter((item) => item !== null);
          if (filteredVideos?.length !== data?.length) {
            await updateDoc(docRef, { data: filteredVideos });
          }
        }
      });
    }
  },

  async checkAndDeleteInvalidOrExpiredUrls(email) {
    await this.deleteExpiredImages("BgGeneration", email);
    await this.deleteExpiredImages("ImageEditor", email);
    // await this.deleteExpiredVideos("VideoMockup", email);
  },

  async getDownloadCount(email) {
    try {
      const encryptedData = localStorage.getItem("downloadCount");
      if (encryptedData) {
        const decrypted = await decryptData(
          encryptedData,
          import.meta.env.VITE_ENCRYPTION_KEY,
        );
        return {
          data: JSON.parse(decrypted),
        };
      } else {
        if (email) {
          const db = getFirestore();
          const docRef = doc(db, "DownloadTracking", email);
          const docSnap = await getDoc(docRef);
          const data = docSnap.data();
          if (data) {
            const downloadData = {
              downloadCount: data?.downloadCount ?? 0,
              downloadTimestamp: data?.downloadTimestamp,
            };
            const encrypted = await encryptData(
              downloadData,
              import.meta.env.VITE_ENCRYPTION_KEY,
            );
            localStorage.setItem("downloadCount", JSON.stringify(encrypted));
            return {
              data: {
                downloadCount: data?.downloadCount ?? 0,
                downloadTimestamp: data?.downloadTimestamp,
              },
            };
          } else return null;
        }
      }
    } catch (e) {
      localStorage.removeItem("downloadCount");
      console.error(e);
    }
  },

  async updateDownloadCount(email, count) {
    try {
      const timestamp = new Date().getTime();
      const data = {
        downloadCount: count,
        downloadTimestamp: timestamp,
      };
      const encrypted = await encryptData(
        data,
        import.meta.env.VITE_ENCRYPTION_KEY,
      );
      localStorage.setItem("downloadCount", JSON.stringify(encrypted));
      const db = getFirestore();
      const docRef = doc(db, "DownloadTracking", email);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          downloadCount: count,
          downloadTimestamp: timestamp,
        });
      } else {
        await setDoc(docRef, {
          downloadCount: count,
          downloadTimestamp: timestamp,
          created_at: timestamp,
        });
      }
    } catch (e) {
      localStorage.removeItem("downloadCount");
      console.error(e);
    }
  },

  async resetDownloadCount(email) {
    const currentTime = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    try {
      const encryptedData = localStorage.getItem("downloadCount");
      if (encryptedData) {
        const decrypted = await decryptData(
          encryptedData,
          import.meta.env.VITE_ENCRYPTION_KEY,
        );
        const data = JSON.parse(decrypted);
        if (currentTime - data?.downloadTimestamp >= oneDay) {
          localStorage.removeItem("downloadCount");
        }
      } else {
        const db = getFirestore();
        const docRef = doc(db, "DownloadTracking", email);
        const docSnap = await getDoc(docRef);
        const data = docSnap.data();
        if (currentTime - data?.downloadTimestamp >= oneDay) {
          await updateDoc(docRef, {
            downloadCount: 0,
            downloadTimestamp: null,
          });
          localStorage.removeItem("downloadCount");
        }
      }
    } catch (e) {
      console.error(e);
      localStorage.removeItem("downloadCount");
    }
  },

  async setFreeCredits(email) {
    const credits = import.meta.env.VITE_FREE_CREDITS;
    const db = getFirestore();
    const docRef = doc(db, "Users", email);
    await setDoc(docRef, {
      credits: parseInt(credits),
      user_created: Date.now(),
    });
  },

  async getVideoMockupStatus(email, callback) {
    const db = getFirestore();
    const docRef = doc(db, "VideoMockup", email);

    try {
      const unsubscribe = onSnapshot(
        docRef,
        { includeMetadataChanges: false },
        (docSnap) => {
          if (docSnap.exists() && !docSnap.metadata.hasPendingWrites) {
            const data = docSnap.data();
            if (data && data?.data && data?.data?.length) {
              const res = [...data.data].reverse();
              callback(res);
            } else {
              callback(null); // No images found
            }
          } else {
            callback(null); // Document does not exist
          }
        },
      );

      // Return the unsubscribe function to stop listening when needed
      return unsubscribe;
    } catch (e) {
      console.error("Error setting up listener:", e);
    }
  },

  async getVideoMockups(email) {
    try {
      const db = getFirestore();
      const docRef = doc(db, "VideoMockup", email);
      const docSnap = await getDoc(docRef);
      const data = docSnap.data();
      if (data && data?.data && data?.data?.length) {
        const res = [...data.data].reverse();
        return res;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
    }
  },
};
